import { useEffect, useState } from "react";
import {
  Smartphone, Check, ChevronLeft, Loader2, Upload, X, Sparkles, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BRAND_LABEL = {
  apple: "Apple", samsung: "SAMSUNG", xiaomi: "Xiaomi",
  huawei: "HUAWEI", google: "Google", honor: "HONOR", other: "+",
};

const CONDITIONS = [
  { value: 5, label: "Wie neu", desc: "Kaum genutzt, Originalverpackung" },
  { value: 4, label: "Sehr gut", desc: "Minimale Kratzer, einwandfrei" },
  { value: 3, label: "Normal", desc: "Leichte Gebrauchsspuren" },
  { value: 2, label: "Gebraucht", desc: "Sichtbare Kratzer, kleine Dellen" },
  { value: 1, label: "Defekt", desc: "Display gesprungen, Schäden" },
];

const ACCESSORIES = [
  "Originalverpackung",
  "Ladekabel",
  "Netzteil",
  "Kopfhörer",
  "Hülle",
  "Schutzfolie",
];

const STORAGE_OPTIONS = ["64 GB", "128 GB", "256 GB", "512 GB", "1 TB"];

export default function BuybackPage() {
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [sel, setSel] = useState({
    brand: null,
    model: null,
    storage: "",
    condition: 4,
    accessories: [],
    notes: "",
    images: [],          // [{url, filename}]
  });
  const [search, setSearch] = useState("");
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);  // {min_price, max_price, reasoning}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "" });
  const [done, setDone] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);

  useEffect(() => { api.get("/catalog/brands").then((r) => setBrands(r.data)); }, []);
  useEffect(() => {
    if (sel.brand) api.get(`/catalog/models/${sel.brand.id}`).then((r) => setModels(r.data));
  }, [sel.brand]);

  const pickBrand = (b) => {
    setSel((s) => ({ ...s, brand: b, model: null }));
    setSearch("");
    setTimeout(() => setStep(2), 200);
  };
  const pickModel = (m) => {
    setSel((s) => ({ ...s, model: m }));
    setTimeout(() => setStep(3), 200);
  };
  const toggleAccessory = (a) => {
    setSel((s) => ({
      ...s,
      accessories: s.accessories.includes(a)
        ? s.accessories.filter((x) => x !== a)
        : [...s.accessories, a],
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (sel.images.length + files.length > 3) {
      toast.error("Maximal 3 Fotos");
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/buyback/upload-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setSel((s) => ({ ...s, images: [...s.images, { url: r.data.url, filename: r.data.filename }] }));
      }
    } catch (err) {
      toast.error("Foto-Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setSel((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
  };

  const requestEstimate = async () => {
    setEstimating(true);
    try {
      const r = await api.post("/buyback/estimate", {
        brand: sel.brand.name,
        model: sel.model,
        storage: sel.storage,
        condition: sel.condition,
        accessories: sel.accessories,
        notes: sel.notes,
      });
      setEstimate(r.data);
      setStep(4);
    } catch (err) {
      toast.error("KI-Schätzung fehlgeschlagen — bitte erneut versuchen");
    } finally {
      setEstimating(false);
    }
  };

  const submit = async () => {
    if (!form.customer_name || !form.phone) {
      toast.error("Name und Telefon sind Pflichtfelder");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/buyback", {
        brand: sel.brand.name,
        model: sel.model,
        storage: sel.storage,
        condition: sel.condition,
        accessories: sel.accessories,
        notes: sel.notes,
        images: sel.images.map((i) => i.url),
        ai_estimate: estimate,
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
      });
      setSubmittedId(r.data.id);
      setDone(true);
      toast.success("Anfrage gesendet! Wir melden uns innerhalb 24h.");
    } catch (err) {
      toast.error("Senden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSel({ brand: null, model: null, storage: "", condition: 4, accessories: [], notes: "", images: [] });
    setForm({ customer_name: "", phone: "", email: "" });
    setEstimate(null);
    setDone(false);
    setSubmittedId(null);
    setStep(1);
  };

  if (done) {
    return (
      <section className="pt-32 pb-24 min-h-screen bg-[#0A0A0B]" data-testid="buyback-success-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-6 border border-emerald-500/30">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white">Anfrage erhalten!</h1>
          <p className="text-zinc-400 mt-4 text-lg leading-relaxed">
            Vielen Dank! Wir prüfen deine Angaben und melden uns innerhalb von 24 Stunden mit einem
            unverbindlichen Ankaufsangebot bei dir.
          </p>
          {submittedId && (
            <p className="text-xs text-zinc-600 mt-3 font-mono" data-testid="buyback-reference">Referenz: {submittedId.slice(0, 8).toUpperCase()}</p>
          )}
          <Button onClick={reset} variant="outline" className="mt-8 rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white" data-testid="buyback-new-request">
            Weiteres Gerät anbieten
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-28 md:pt-32 pb-20 bg-[#0A0A0B] min-h-screen" data-testid="buyback-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs">
            <Sparkles className="w-3.5 h-3.5 text-[#FF2D40]" />
            <span className="text-zinc-200 font-medium">KI-Sofortschätzung</span>
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight text-white mt-5 leading-[1.02]">
            Wir kaufen<br/>
            <span className="bg-gradient-to-r from-[#FF2D40] via-[#DC1928] to-[#FF2D40] bg-clip-text text-transparent">dein Handy.</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-xl mx-auto mt-5 leading-relaxed">
            Egal ob neu, gebraucht oder defekt — beschreibe dein Gerät, unsere KI schätzt sofort
            den Ankaufspreis. Unverbindliches Angebot innerhalb 24h.
          </p>
        </div>

        {/* Stepper */}
        <div className="bg-[#141417] border border-white/5 rounded-3xl overflow-hidden" data-testid="buyback-configurator">
          <div className="px-6 md:px-8 pt-8 pb-4 flex items-center gap-3">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="flex items-center flex-1">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                    step >= n ? "bg-[#DC1928] text-white" : "bg-white/5 text-zinc-500 border border-white/10"
                  }`}
                  data-testid={`buyback-step-${n}`}
                >
                  {step > n ? <Check className="w-4 h-4" /> : n}
                </div>
                {n < 4 && <div className={`flex-1 h-px mx-2 ${step > n ? "bg-[#DC1928]" : "bg-white/10"}`}></div>}
              </div>
            ))}
          </div>

          {/* Step 1: Brand */}
          {step === 1 && (
            <div className="px-6 md:px-8 pb-8 fade-up">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Schritt 1 von 4</div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1 text-white">Welche Marke?</h3>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {brands.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => pickBrand(b)}
                    className="group h-28 rounded-2xl border border-white/10 hover:border-white/30 bg-[#1C1C20] transition-all flex flex-col items-center justify-center gap-2"
                    data-testid={`buyback-brand-${b.id}`}
                  >
                    <div className="font-display font-bold text-2xl text-white tracking-tight">{BRAND_LABEL[b.id] || b.name}</div>
                    <div className="text-xs text-zinc-500">{b.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Model */}
          {step === 2 && (
            <div className="px-6 md:px-8 pb-8 fade-up">
              <button onClick={() => setStep(1)} className="text-sm text-zinc-500 flex items-center gap-1 hover:text-white mb-4" data-testid="buyback-back-brand">
                <ChevronLeft className="w-4 h-4" /> {sel.brand?.name}
              </button>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Schritt 2 von 4</div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1 text-white">Welches Modell?</h3>

              <div className="relative mt-5">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Modell suchen…"
                  className="w-full h-11 pl-10 pr-4 rounded-xl bg-[#0A0A0B] border border-white/10 text-white placeholder:text-zinc-600 focus:border-[#DC1928] focus:outline-none text-sm"
                  data-testid="buyback-model-search"
                />
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                {models
                  .filter((m) => !search || m.toLowerCase().includes(search.toLowerCase()))
                  .map((m) => (
                    <button
                      key={m}
                      onClick={() => pickModel(m)}
                      className="text-left px-4 py-3 rounded-xl border border-white/10 hover:border-white/30 bg-[#1C1C20] text-zinc-300 transition-all"
                      data-testid={`buyback-model-${m.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div className="text-sm font-medium">{m}</div>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* Step 3: Storage, condition, accessories, photos, notes */}
          {step === 3 && (
            <div className="px-6 md:px-8 pb-8 fade-up space-y-7">
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <button onClick={() => setStep(2)} className="hover:text-white flex items-center gap-1" data-testid="buyback-back-model">
                  <ChevronLeft className="w-4 h-4" /> {sel.brand?.name}
                </button>
                <span>·</span>
                <span className="text-white font-medium">{sel.model}</span>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Schritt 3 von 4</div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold mt-1 text-white">Zustand & Zubehör</h3>
              </div>

              {/* Storage */}
              <div>
                <Label className="text-zinc-400 text-sm font-semibold">Speichergröße</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="buyback-storage">
                  {STORAGE_OPTIONS.map((opt) => {
                    const active = sel.storage === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setSel((s) => ({ ...s, storage: active ? "" : opt }))}
                        className={`px-4 h-10 rounded-full border text-sm transition-all ${
                          active
                            ? "border-[#DC1928] bg-[#DC1928]/15 text-white"
                            : "border-white/10 bg-[#1C1C20] text-zinc-400 hover:border-white/30 hover:text-white"
                        }`}
                        data-testid={`buyback-storage-${opt.replace(/\s+/g, "")}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Condition */}
              <div>
                <Label className="text-zinc-400 text-sm font-semibold">Zustand</Label>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="buyback-condition">
                  {CONDITIONS.map((c) => {
                    const active = sel.condition === c.value;
                    return (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setSel((s) => ({ ...s, condition: c.value }))}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          active
                            ? "border-[#DC1928] bg-[#DC1928]/15"
                            : "border-white/10 bg-[#1C1C20] hover:border-white/30"
                        }`}
                        data-testid={`buyback-condition-${c.value}`}
                      >
                        <div className={`text-sm font-semibold ${active ? "text-white" : "text-zinc-300"}`}>{c.label}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight">{c.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Accessories */}
              <div>
                <Label className="text-zinc-400 text-sm font-semibold">Zubehör vorhanden</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="buyback-accessories">
                  {ACCESSORIES.map((a) => {
                    const active = sel.accessories.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggleAccessory(a)}
                        className={`px-3 h-9 rounded-full border text-xs transition-all flex items-center gap-1.5 ${
                          active
                            ? "border-[#DC1928] bg-[#DC1928]/15 text-white"
                            : "border-white/10 bg-[#1C1C20] text-zinc-400 hover:border-white/30 hover:text-white"
                        }`}
                        data-testid={`buyback-acc-${a.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {a}
                        {active && <Check className="w-3 h-3" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Photos */}
              <div>
                <Label className="text-zinc-400 text-sm font-semibold">Fotos (optional, max. 3)</Label>
                <div className="mt-2 grid grid-cols-3 gap-3" data-testid="buyback-photos">
                  {sel.images.map((img, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-white/10 bg-[#0A0A0B]">
                      <img src={img.url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 backdrop-blur text-white hover:bg-red-500/80 flex items-center justify-center"
                        aria-label="Foto entfernen"
                        data-testid={`buyback-photo-remove-${i}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {sel.images.length < 3 && (
                    <label className="aspect-square rounded-xl border border-dashed border-white/20 hover:border-white/40 bg-[#1C1C20] hover:bg-[#21212a] cursor-pointer flex flex-col items-center justify-center gap-2 text-zinc-500 hover:text-white transition-colors" data-testid="buyback-photo-upload">
                      {uploading ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6" />
                          <span className="text-xs">Foto hochladen</span>
                        </>
                      )}
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                    </label>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-zinc-400 text-sm font-semibold">Anmerkungen (optional)</Label>
                <Textarea
                  value={sel.notes}
                  onChange={(e) => setSel((s) => ({ ...s, notes: e.target.value }))}
                  placeholder="Display gesprungen? Akku schwach? Sonstige Defekte?"
                  rows={3}
                  className="mt-2 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="buyback-notes"
                />
              </div>

              <Button
                onClick={requestEstimate}
                disabled={estimating}
                className="w-full bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-12 text-base btn-lift glow-red-sm"
                data-testid="buyback-estimate-btn"
              >
                {estimating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> KI berechnet…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> KI-Schätzung anfordern</>
                )}
              </Button>
            </div>
          )}

          {/* Step 4: AI estimate + contact */}
          {step === 4 && estimate && (
            <div className="px-6 md:px-8 pb-8 fade-up">
              <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
                <button onClick={() => setStep(3)} className="hover:text-white flex items-center gap-1" data-testid="buyback-back-details">
                  <ChevronLeft className="w-4 h-4" /> Zurück
                </button>
              </div>

              <div className="relative bg-gradient-to-br from-[#1a0509] to-[#0A0A0B] p-7 md:p-9 rounded-2xl border border-[#DC1928]/30 overflow-hidden mb-6" data-testid="buyback-estimate-card">
                <div className="absolute inset-0 bg-[#DC1928]/[0.05] pointer-events-none"></div>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#FF2D40]" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#FF2D40] font-bold">KI-Schätzung</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold mt-2 text-white">
                    {sel.brand.name} {sel.model}
                    {sel.storage && <span className="text-zinc-500"> · {sel.storage}</span>}
                  </h3>

                  <div className="mt-5 flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm text-zinc-500">Ankaufspreis:</span>
                    <span className="font-display text-5xl md:text-6xl font-bold text-white numeric" data-testid="buyback-estimate-range">
                      €{estimate.min_price}–{estimate.max_price}
                    </span>
                  </div>

                  {estimate.reasoning && (
                    <p className="text-sm text-zinc-400 mt-4 leading-relaxed max-w-2xl" data-testid="buyback-estimate-reasoning">
                      {estimate.reasoning}
                    </p>
                  )}
                  <p className="text-[11px] text-zinc-600 mt-3 italic">
                    Unverbindliche Schätzung. Endgültiger Preis nach Vor-Ort-Begutachtung.
                  </p>
                </div>
              </div>

              <div className="bg-[#141417] rounded-2xl p-6 border border-white/10 space-y-3">
                <h4 className="font-display font-semibold text-lg text-white">Kontaktdaten</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-400">Name *</Label>
                    <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Max Mustermann" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="buyback-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Telefon *</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+43 660 1234567" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="buyback-phone" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">E-Mail (optional)</Label>
                    <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@beispiel.at" className="bg-[#0A0A0B] border-white/10 text-white" data-testid="buyback-email" />
                  </div>
                  <Button onClick={submit} disabled={submitting} className="w-full bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full h-11 mt-2 btn-lift" data-testid="buyback-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unverbindliches Angebot anfragen"}
                  </Button>
                  <p className="text-[10px] text-zinc-500 text-center">Unverbindlich · Antwort innerhalb 24h</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
